import React, { useState, useEffect } from 'react'
import { Redirect, useLocation, useParams } from 'react-router-dom'
import { deposit } from '@ally/data-selectors-afg-accounts'
import { BucketDeeplinkScenario, BucketDeeplinkScenarios } from '../../types'
import { useHostServices } from '../../HostServices'
import { AccountDetailsSkeletonLoader } from '../../components/common/AccountDetailSkeletonLoader'

/* There isn't much actual logic to test here, and setting up
all of the react-router-dom mocks would be more effort than it's worth */

/* istanbul ignore file */

export const isValidScenario = (
  scenario: BucketDeeplinkScenario,
): scenario is BucketDeeplinkScenario => {
  return (
    Object.values(BucketDeeplinkScenarios) as BucketDeeplinkScenario[]
  ).includes(scenario)
}

export const SpendingBucketsRedirector: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const { deeplink } = useParams<{ deeplink: BucketDeeplinkScenario }>()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const paramAccountId = params.get('accountId')
  const toolkitBucketId = params.get('bucketId')

  const { hostData } = useHostServices()
  const afgAccountsStatus = hostData.afgAccounts.status.loadState
  const checkingAccounts =
    deposit.nonRetirement
      .getDDA(hostData)
      .filter(account => account.status.toLowerCase() === 'active') || []

  useEffect(() => {
    if (afgAccountsStatus === 'loaded') {
      setLoading(false)
    }
  }, [afgAccountsStatus])

  // deeplink scenarios
  // `list` is a fallback deeplink
  if (deeplink && deeplink !== 'list') {
    // found deeplink param but not a valid scenario, redirect to AD
    if (!isValidScenario(deeplink)) {
      return <Redirect to={`/account/${paramAccountId}/details`} />
    }
    // account specific deeplinking requires an accountId
    if (!paramAccountId) {
      return <Redirect to="/dashboard" />
    }

    return (
      <Redirect
        to={{
          pathname: `/account/${paramAccountId}/details`,
          state: {
            deeplink,
            toolkitBucketId,
          },
        }}
      />
    )
  }

  // Default deeplink scenarios
  // ex. /account/spending-buckets or /account/spending-buckets/list
  if (loading) {
    return <AccountDetailsSkeletonLoader />
  }

  // no checking accounts
  if (checkingAccounts.length === 0) {
    return <Redirect to="/dashboard" />
  }

  // No deeplink, done loading -> fetch checking account with highest balance
  const foundAccount = checkingAccounts.reduce((accumulator, account) => {
    if (!accumulator) {
      return account
    }

    const prevHighest = accumulator.balance?.available || 0
    const current = account.balance?.available || 0

    return prevHighest < current ? account : accumulator
  })

  // No deeplinks passed in route, use foundAccountId and default deeplink
  const accountId = foundAccount.id

  return (
    <Redirect
      to={{
        pathname: `/account/${accountId}/details`,
        state: {
          deeplink: BucketDeeplinkScenarios.Default,
          toolkitBucketId,
        },
      }}
    />
  )
}
